/**
 * BuildingsTable — building cards with the PR-2 enrichment surfaced:
 * rent|sale split counts (the Rails "rent | buy" pair), near-MTR badge, and
 * (SEO F2) the four NAMED closest POIs. Links stay estate-scoped (the
 * listings filter contract deliberately has no buildingId param).
 *
 * Before F2 this rendered one bare "250 m to MTR" line and dropped the
 * school/hospital/car-park distances entirely — they were on the wire but
 * unnamed, and an unnamed distance is not worth a line. Now each POI carries
 * its own name, so all four are worth rendering and each reads as a fact.
 */
import { Link } from '@/shared/i18n/routing';
import { useTranslations } from '@/shared/i18n/useTranslations';
import type { BuildingData, NearbyPoiData } from '@/shared/lib/types/estates';
import { projectLocalized } from '@letsgethome/lib/i18n';
import { routes } from '@letsgethome/routes';
import { plaintextString, type EstateId, type Locale } from '@letsgethome/types/branded';

type Props = {
  buildings: BuildingData[];
  estateId: EstateId;
  locale: Locale;
};

/**
 * The four POI slots in render order, each with its own label key.
 *
 * Typed against `BuildingData`'s POI-valued keys, so a fifth POI added to
 * the wire cannot be forgotten here: the `satisfies` fails until the slot is
 * listed. MTR leads because it is the proximity HK renters actually search
 * on ("近咩地鐵站").
 */
const POI_SLOTS = [
  { field: 'closestMtrStation', labelKey: 'facts.poi.mtr' },
  { field: 'closestSchool', labelKey: 'facts.poi.school' },
  { field: 'closestHospital', labelKey: 'facts.poi.hospital' },
  { field: 'closestCarPark', labelKey: 'facts.poi.carPark' },
] as const satisfies ReadonlyArray<{
  field: {
    [K in keyof BuildingData]-?: NearbyPoiData | undefined extends BuildingData[K] ? K : never;
  }[keyof BuildingData];
  labelKey: string;
}>;

export function BuildingsTable({ buildings, estateId, locale }: Props) {
  const t = useTranslations('estates.detail');
  if (buildings.length === 0) {
    return null;
  }
  return (
    <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <h2 className="mb-6 text-2xl font-bold">
        {t('buildingsCount', { count: buildings.length })}
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {buildings.map((building) => {
          // ONE label, and it is the DERIVED one. This card used to render the
          // AUTHORED `name` as its title with `fullName` as a muted subtitle
          // "when it differs" — so for a block-only building (nine in ten of
          // them) it emitted an EMPTY title paragraph with `Tower 1` in grey
          // underneath. Since LET-121 `full_name` is a GENERATED column with a
          // CHECK that refuses a blank, so the title cannot be empty and the
          // subtitle has nothing left to say. Not `qualifiedName`: this table
          // lives on the estate's own page, which names the estate in its h1.
          const name = plaintextString(projectLocalized(building.fullName, locale));
          return (
            <div key={building.id} className="rounded-xl bg-muted p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-foreground">{name}</p>
                </div>
                {building.nearMtr && (
                  <span className="ml-2 shrink-0 rounded-full bg-info/10 px-2 py-0.5 text-xs font-medium text-info-text">
                    {t('nearMtr')}
                  </span>
                )}
              </div>
              <dl className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                {POI_SLOTS.map(({ field, labelKey }) => {
                  const poi = building[field];
                  if (!poi) {
                    return null;
                  }
                  return (
                    <div key={field} className="flex gap-1">
                      <dt className="shrink-0">{t(labelKey)}</dt>
                      <dd className="truncate">
                        {t('facts.poi.value', {
                          name: plaintextString(projectLocalized(poi.name, locale)),
                          distance: poi.distanceM,
                        })}
                      </dd>
                    </div>
                  );
                })}
              </dl>
              <div className="mt-3 flex gap-2 text-sm">
                <Link
                  href={routes.listings.rent({ estateId })}
                  prefetch="intent"
                  className="rounded-full bg-background px-3 py-1 font-medium text-success-text transition-colors hover:bg-background/80"
                >
                  {t('buildings.forRent')} · {building.activeRentCount}
                </Link>
                <Link
                  href={routes.listings.buy({ estateId })}
                  prefetch="intent"
                  className="rounded-full bg-background px-3 py-1 font-medium text-info-text transition-colors hover:bg-background/80"
                >
                  {t('buildings.forSale')} · {building.activeSaleCount}
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
